import type { UseQueryResult } from '@tanstack/react-query'
import { useQueries, useQueryClient } from '@tanstack/react-query'
import { useMemo, useRef } from 'react'

import { useGlobalState } from '@/hooks/useGlobalState'
import type { GetResourcesResponse, GetStepsResponse, ProcessProgress, ResourceType } from '@/services/api/api.types'

export type StepWithValidation = GetStepsResponse[0] & {
  valid: boolean
  contentType?: ResourceType
  inProcess?: boolean | null
  processProgress?: ProcessProgress['processProgress']
  processProgressError?: string
  transcodeCompleted?: boolean
  transcodeSuccess?: boolean
}

type CombinedResourceResult = {
  data: GetResourcesResponse | undefined
  isSuccess: boolean
  isError: boolean
  error: Error | null
}

type CombinedContentResult = {
  data:
    | {
        inProcess?: boolean
        transcodeCompleted?: boolean
        transcodeSuccess?: boolean
        processProgress?: ProcessProgress['processProgress']
      }
    | undefined
  isSuccess: boolean
}

export interface StepsWithValidationResult {
  data: StepWithValidation[]
  isLoading: boolean
  isError: boolean
  error: Error | null
  isSuccess: boolean
  refetch: () => Promise<{ data?: StepWithValidation[] }>
}

/**
 * Custom hook that calculates step validation status by batching resource queries.
 * Uses React Query's useQueries to fetch resources for all steps in parallel with caching.
 */
export function useStepsWithValidation(stepsQuery: {
  data?: GetStepsResponse
  isSuccess: boolean
  isLoading: boolean
  isError: boolean
  error: Error | null
  refetch: () => Promise<{ data?: GetStepsResponse }>
}): StepsWithValidationResult {
  const { api } = useGlobalState()
  const queryClient = useQueryClient()

  // Get the steps data
  const steps = stepsQuery.data ?? []

  // Fetch all resources (including source files) directly from endpoint.
  // `combine` with structural sharing ensures the returned value only gets a
  // new reference when actual query data changes — not on every render cycle.
  // `notifyOnChangeProps` limits which query state changes trigger re-renders.
  const resourceResults = useQueries({
    queries: steps.map(step => ({
      queryKey: ['step', 'resources', 'all', step.stepId],
      queryFn: async () => {
        return api.request<GetResourcesResponse>('/cm/get_resources', 'get', {
          step_id: step.stepId,
        })
      },
      enabled: !!step.stepId && stepsQuery.isSuccess,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      notifyOnChangeProps: ['data', 'error', 'isSuccess'] as Array<keyof UseQueryResult>,
    })),
    combine: (results: UseQueryResult[]): CombinedResourceResult[] =>
      results.map(r => ({
        data: r.data as GetResourcesResponse | undefined,
        isSuccess: r.isSuccess,
        isError: r.isError,
        error: r.error,
      })),
  })

  // Fetch content metadata per step for transcoding status.
  // Same combine + notifyOnChangeProps strategy as resource queries.
  const contentResults = useQueries({
    queries: steps.map(step => ({
      queryKey: ['step', 'content', 'meta', step.stepId],
      queryFn: () => api.getContentMeta({ step_id: step.stepId }),
      enabled: !!step.stepId && stepsQuery.isSuccess,
      staleTime: 30 * 1000,
      gcTime: 5 * 60 * 1000,
      notifyOnChangeProps: ['data', 'error', 'isSuccess'] as Array<keyof UseQueryResult>,
      refetchInterval: (query: { state: { data?: unknown } }) => {
        const data = query.state.data as
          | { inProcess?: boolean; transcodeCompleted?: boolean; processProgress?: ProcessProgress['processProgress'] }
          | undefined
        if (data?.processProgress?.error) {
          return false
        }
        if (data && (data.inProcess === true || data.transcodeCompleted === false)) {
          return 5000
        }
        return false
      },
    })),
    combine: (results: UseQueryResult[]): CombinedContentResult[] =>
      results.map(r => ({
        data: r.data as CombinedContentResult['data'],
        isSuccess: r.isSuccess,
      })),
  })

  // Compute the validation status. Dependencies are now structurally-shared
  // combined results, so this memo only recomputes when actual data changes.
  const stepsWithValidation = useMemo(() => {
    if (!stepsQuery.isSuccess) {
      return []
    }

    return steps.map((step, index) => {
      const resourceResult = resourceResults[index]
      const contentResult = contentResults[index]

      const contentInProcess = contentResult?.data?.inProcess
      const inProcess = step.inProcess === true || contentInProcess === true
      const processProgress = contentResult?.data?.processProgress
      const processProgressError = processProgress?.error?.message
      const transcodeCompleted = contentResult?.data?.transcodeCompleted
      const transcodeSuccess = contentResult?.data?.transcodeSuccess

      if (!resourceResult?.isSuccess) {
        return {
          ...step,
          valid: !!step.name,
          inProcess,
          processProgress,
          processProgressError,
          transcodeCompleted,
          transcodeSuccess,
        }
      }

      const allResources = resourceResult.data ?? []
      const sourceFile = allResources.find(r => r.isSourceFile)

      return {
        ...step,
        valid: !!step.name,
        contentType: sourceFile?.type,
        inProcess,
        processProgress,
        processProgressError,
        transcodeCompleted,
        transcodeSuccess,
      }
    })
  }, [steps, resourceResults, contentResults, stepsQuery.isSuccess])

  // Stable refetch function that doesn't change reference across renders.
  // All mutable dependencies are accessed via refs so the closure never goes stale.
  const stepsRef = useRef(steps)
  stepsRef.current = steps
  const refetchStepsRef = useRef(stepsQuery.refetch)
  refetchStepsRef.current = stepsQuery.refetch
  const refetch = useRef(async () => {
    const stepsResult = await refetchStepsRef.current()

    stepsRef.current.forEach(step => {
      queryClient.invalidateQueries({ queryKey: ['step', 'resources', 'all', step.stepId] })
      queryClient.invalidateQueries({ queryKey: ['step', 'content', 'meta', step.stepId] })
    })

    if (stepsResult.data) {
      const enhancedSteps = stepsResult.data.map(step => ({
        ...step,
        valid: !!step.name,
      }))
      return { data: enhancedSteps }
    }

    return { data: undefined }
  }).current

  // Derive error state from resource results (already structurally shared).
  const resourceError = useMemo(() => {
    const hasError = resourceResults.some(q => q.isError)
    const firstError = resourceResults.find(q => q.error)?.error ?? null
    return { hasError, firstError }
  }, [resourceResults])

  // Only recompute result when data, loading, or error state actually changes.
  // Background refetch toggles (isFetching) are intentionally excluded — each
  // query's isFetching toggle was causing a full tree re-render with many steps.
  const result = useMemo((): StepsWithValidationResult => {
    return {
      data: stepsWithValidation,
      isLoading: stepsQuery.isLoading,
      isError: stepsQuery.isError || resourceError.hasError,
      error: stepsQuery.error || resourceError.firstError,
      isSuccess: stepsQuery.isSuccess,
      refetch,
    }
  }, [
    stepsWithValidation,
    stepsQuery.isLoading,
    stepsQuery.isError,
    stepsQuery.error,
    stepsQuery.isSuccess,
    resourceError,
    refetch,
  ])

  return result
}
